import { useState, type ComponentProps, type ElementType } from 'react';
import { Box, Flex, Grid, Icon, Spinner } from '@chakra-ui/react';
import { SanitizedText } from '@/ui/SanitizedDom';
import { getDelayById } from '@/pages/mco/steps/components/step/delay';
import { AttachmentPreview } from '@/components/outreach-attachment/AttachmentPreview';
import AttachmentIcon from '@/assets/icons/attachements/AttachmentIcon';

import EmailIcon from '@/assets/icons/colored/Email';
import LiConnectionRequestIcon from '@/assets/icons/colored/ConnectionRequest';
import LiInMailIcon from '@/assets/icons/colored/Linkedin';

import { useGetStepByIdQuery } from '@/store/query/mco-step';
import { useInViewport } from 'ahooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { GradientTag } from '@/components/ai/GradientTag';
import { KText } from '@/ui-library/components/text';

interface StepAttachment {
  name?: string;
  url?: string;
}

interface StepTemplate {
  subject?: string;
  body?: string;
  attachments?: StepAttachment[];
  ai_goals?: {
    role?: unknown[];
  };
}

interface StepData {
  id?: string | number;
  step?: number;
  name?: string;
  channel?: string;
  step_interval?: number;
  template?: StepTemplate;
  stats?: {
    reply_rate?: number;
  };
}

interface Blueprint {
  outreach?: {
    id?: string | number;
    steps?: StepData[];
  };
}

const StepIcon = ({ step = {} }: { step?: StepData }) => {
  const stepIcon = new Map<string, ElementType>([
    ['email', EmailIcon],
    ['li_connection', LiConnectionRequestIcon],
    ['li_inmail', LiInMailIcon],
  ]);
  return (
    <Icon
      width="24px"
      height="24px"
      as={stepIcon?.get(step?.channel as string)}
      color="#2D64BC"
      borderRadius="8px"
      mr="8px"
    />
  );
};

const StepView = ({
  step = {},
  onPreviewView = () => {},
  isLoadingMore = true,
}: {
  step?: StepData;
  onPreviewView?: (step: StepData) => void;
  isLoadingMore?: boolean;
}) => {
  return (
    <Box
      mt="20px"
      bg="secondary-25"
      cursor="pointer"
      borderRadius="5px"
      onClick={() => {
        onPreviewView(step);
      }}
    >
      <Box px="24px">
        {step?.template?.subject && (
          <Flex borderBottom="1px solid #ECE5DD" pt="24px" pb="18px">
            <KText size="sm-semi-bold" color="secondary-900">
              Subject
            </KText>
            <KText ml="4px" size="sm-regular" flex="1" color="secondary-900">
              {step?.template?.subject}
            </KText>
            <Flex>
              {(step?.template?.attachments?.length ?? 0) > 0 && (
                <Flex alignItems="center">
                  <Icon as={AttachmentIcon} w="10px" h="10px" />
                  <KText color="#000" size="sm-regular" ml="4px">
                    {step?.template?.attachments?.length}
                  </KText>
                </Flex>
              )}
              {(step?.stats?.reply_rate ?? 0) > 0 && (
                <Flex
                  bg="#E6ECF2"
                  color="#525252"
                  ml="12px"
                  fontSize="12px"
                  fontWeight="400"
                  p="4px 6px"
                  borderRadius="5px"
                >
                  {step?.stats?.reply_rate}% Reply Rate
                </Flex>
              )}
            </Flex>
          </Flex>
        )}
        <Box py="24px" className="editor-style">
          <SanitizedText
            whiteSpace="pre-wrap"
            // 'muted' predates KText's typed variant union; the cast keeps
            // the legacy runtime styling untouched.
            variant={
              'muted' as unknown as ComponentProps<typeof KText>['variant']
            }
            html={step?.template?.body}
            color="secondary-700"
            size="sm-regular"
          />

          {(step?.template?.attachments?.length ?? 0) > 0 && (
            <Grid
              pt="12px"
              mt="12px"
              gap="10px"
              borderTop="1px solid #E5E5E5 "
              templateColumns="repeat(3, 1fr)"
            >
              {step?.template?.attachments?.map((file, idx) => (
                <AttachmentPreview
                  key={idx}
                  file={file}
                  AfterContent={undefined}
                />
              ))}
            </Grid>
          )}
        </Box>
        {isLoadingMore && <Spinner mb="12px" color="#0043CE" />}
      </Box>
    </Box>
  );
};

const BlueprintStep = ({
  blueprint = {},
  step = {},
}: {
  blueprint?: Blueprint;
  step?: StepData;
}) => {
  const [view, setView] = useState(false);
  const { data, isLoading } = useGetStepByIdQuery(
    {
      outreachId: blueprint?.outreach?.id,
      stepId: step?.id,
    },
    { skip: !step?.id || !blueprint?.outreach?.id || !view }
  );
  const delay = getDelayById(step?.step_interval);
  const { pOpenAi } = useKFlags();

  // setState happens in the observer callback (not an effect reacting to the
  // returned inViewport value) so the view latch doesn't cascade renders.
  useInViewport(() => document.getElementById(`step-child-${step?.id}`), {
    threshold: [0, 0.25, 0.5, 0.75, 1],
    root: () => document.getElementById('step-parent'),
    callback: (entry) => {
      if (!entry.isIntersecting) return;
      setView(true);
    },
  });

  return (
    <Box my="24px" position="relative" id={`step-child-${step?.id}`}>
      <Flex alignItems="center">
        <Flex flex="1">
          <KText
            bg="primary-100"
            width="20px"
            height="20px"
            borderRadius="50%"
            as={Flex}
            alignItems="center"
            justifyContent="center"
            color="primary-400"
            size="sm-semi-bold"
          >
            {step?.step}
          </KText>
          <KText ml="10px" fontWeight="semiBold">
            {`
						 Step : ${step?.step}`}
          </KText>
          <KText ml="4px">{step?.name}</KText>
        </Flex>
        {pOpenAi && step?.template?.ai_goals?.role?.length && (
          <GradientTag mr="12px">AI</GradientTag>
        )}
        <StepIcon step={step} />
        <Flex>
          <KText>
            {`
          ${(step?.step_interval ?? 0) > 30 ? 'After' : 'Send'} ${delay?.label}
						`}
          </KText>
        </Flex>
      </Flex>
      <StepView
        step={view && !isLoading ? data : step}
        isLoadingMore={isLoading}
      />
    </Box>
  );
};

export const BlueprintSteps = ({
  blueprint = {},
}: {
  blueprint?: Blueprint;
}) => {
  return (
    <>
      <Box px="24px" h="calc(100% - 156px)" overflow="auto" id="step-parent">
        {blueprint?.outreach?.steps?.map((step) => {
          return (
            <BlueprintStep key={step?.id} blueprint={blueprint} step={step} />
          );
        })}
      </Box>
    </>
  );
};
